import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  createContext,
  useCallback,
  memo,
} from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Stack,
  IconButton,
  alpha,
  Snackbar,
  Alert,
  useTheme,
  Divider,
  Tooltip,
  Fade,
  GlobalStyles,
  useMediaQuery,
  Drawer,
  Dialog,
  TextField,
  InputAdornment,
  Zoom,
} from '@mui/material';
import {
  School as SchoolIcon,
  Receipt as ReceiptIcon,
  TransferWithinAStation as TransferIcon,
  Verified as VerifiedIcon,
  Badge as TeacherBadgeIcon,
  WorkspacePremium as CertificateIcon,
  AttachMoney as SalaryIcon,
  Email as EmailIcon,
  QrCodeScanner as QrScannerIcon,
  TableChart as ExcelIcon,
  CalendarToday as CalendarIcon,
  MenuBook as LessonIcon,
  Edit as ExamIcon,
  Chat as ChatIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  RemoveCircleOutline as PartialIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  Lock as LockIcon,
  People as PeopleIcon,
  CreditCard as CreditCardIcon,
  AssignmentTurnedIn as AssignmentIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  PeopleAlt as CapacityIcon,
  Discount as DiscountIcon,
  PictureAsPdf,
  ContactMail as ContactMailIcon,
  Info,
  ArrowForward as ArrowForwardIcon,
  AutoAwesome as SparkleIcon,
  Bolt as BoltIcon,
  CloudDone as CloudDoneIcon,
  AutoGraph as AutoGraphIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  PlayArrow as PlayIcon,
  Star as StarIcon,
  Shield as ShieldIcon,
  WbSunny as SunIcon,
  NightsStay as MoonIcon,
  Quiz as QuizIcon,                 // ← ADD
  LibraryBooks as TestSeriesIcon,   // ← ADD
  PersonAdd as BulkStudentIcon,     // ← ADD
} from '@mui/icons-material';
import { getDeviceInfo } from '../utils/deviceInfo';
import AuthService from '../Auth/AuthService';
import axios from 'axios';
import Logo from '../assets/logo-round-jmk.png';
import LogoDark from '../assets/dark-logo-round-jmk.png';
import AuthDialog from './AuthDialog';
import OfferLetterGenerator from '../emp-components/emp-card/OfferLetterGenerator';
import ImageToPDFConverter from '../emp-components/emp-card/ImageToPDFConverter';
import ModalView from '../model/ModelView';
import ContactForm from './ContactForm';
import AboutUs from './AboutUs';
import CheckResultPage from './CheckResultPage';

/* ================================================================== */
/*                     GLOBAL ANIMATIONS & UTILS                       */
/* ================================================================== */

const GlobalAnimations = () => (
  <GlobalStyles
    styles={{
      '@keyframes meshMove': {
        '0%, 100%': { transform: 'translate(0%, 0%) rotate(0deg)' },
        '25%': { transform: 'translate(-5%, 5%) rotate(90deg)' },
        '50%': { transform: 'translate(5%, -5%) rotate(180deg)' },
        '75%': { transform: 'translate(-3%, 3%) rotate(270deg)' },
      },
      '@keyframes shimmerSlide': {
        '0%': { backgroundPosition: '-500% 0' },
        '100%': { backgroundPosition: '500% 0' },
      },
      '@keyframes floatSoft': {
        '0%, 100%': { transform: 'translateY(0) rotate(0deg)' },
        '50%': { transform: 'translateY(-15px) rotate(2deg)' },
      },
      '@keyframes pulseGlow': {
        '0%, 100%': { boxShadow: '0 0 0 0 rgba(99,102,241,0.5)' },
        '50%': { boxShadow: '0 0 0 12px rgba(99,102,241,0)' },
      },
      '@keyframes gradientShift': {
        '0%, 100%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
      },
      '@keyframes scanSweep': {
        '0%': { top: 0, opacity: 0 },
        '10%': { opacity: 1 },
        '90%': { opacity: 1 },
        '100%': { top: '100%', opacity: 0 },
      },
      '@keyframes spinSlow': {
        from: { transform: 'rotate(0deg)' },
        to: { transform: 'rotate(360deg)' },
      },
      '@keyframes blinkDot': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.2 },
      },
      '@keyframes typewriter': {
        from: { width: 0 },
        to: { width: '100%' },
      },
      '@keyframes caret': {
        '50%': { borderColor: 'transparent' },
      },
      '@keyframes scrollVert': {
        '0%': { transform: 'translateY(0)' },
        '100%': { transform: 'translateY(-50%)' },
      },
      '@keyframes borderDance': {
        '0%, 100%': { backgroundPosition: '0% 50%' },
        '50%': { backgroundPosition: '100% 50%' },
      },
      '.anim-mesh': { animation: 'meshMove 30s ease-in-out infinite' },
      '.anim-float-soft': { animation: 'floatSoft 6s ease-in-out infinite' },
      '.anim-pulse-glow': { animation: 'pulseGlow 2s ease-in-out infinite' },
      '.anim-gradient-shift': {
        animation: 'gradientShift 4s ease infinite',
        backgroundSize: '200% auto',
      },
      '.anim-spin-slow': { animation: 'spinSlow 8s linear infinite' },
      '.anim-blink-dot': { animation: 'blinkDot 2s ease-in-out infinite' },
      '.anim-scan-sweep': { animation: 'scanSweep 2s ease-in-out infinite' },
      '.anim-border-dance': {
        animation: 'borderDance 4s ease infinite',
        backgroundSize: '300% 300%',
      },
      '.noise-overlay': {
        backgroundImage:
          'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.4\'/%3E%3C/svg%3E")',
        opacity: 0.035,
        pointerEvents: 'none',
        position: 'absolute',
        inset: 0,
      },
      '::selection': {
        background: 'rgba(99,102,241,0.3)',
      },
      '::-webkit-scrollbar': { width: 6, height: 6 },
      '::-webkit-scrollbar-track': { background: 'transparent' },
      '::-webkit-scrollbar-thumb': {
        background: 'rgba(148,163,184,0.4)',
        borderRadius: 3,
      },
      '::-webkit-scrollbar-thumb:hover': { background: 'rgba(148,163,184,0.7)' },
    }}
  />
);

/* ================================================================== */
/*                            HELPERS                                  */
/* ================================================================== */

const formatDuration = (days) => {
  if (days === 365) return 'Yearly';
  if (days === 28 || days === 30) return 'Monthly';
  return `${days} days`;
};

const formatPrice = (price) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'inr',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(price);

const getDiscountedPrice = (plan) => {
  if (!plan.couponDTO) return null;
  const { discountType, discountPercentage, discountAmount } = plan.couponDTO;
  if (discountType === 'PERCENTAGE' && discountPercentage) {
    return plan.price * (1 - discountPercentage / 100);
  }
  if (discountType === 'FLAT' && discountAmount) {
    return Math.max(0, plan.price - discountAmount);
  }
  return null;
};

/* ================================================================== */
/*                          CONTEXT                                    */
/* ================================================================== */

const ColorModeContext = createContext({ toggleColorMode: () => { } });

/* ================================================================== */
/*                       ANIMATED COUNTER                              */
/* ================================================================== */

/* ================================================================== */
/*                       TYPING TEXT (Typewriter)                      */
/* ================================================================== */

const TypingText = memo(function TypingText({
  phrases = [],
  speed = 60,
  pauseMs = 1500,
  deleteSpeed = 30,
  loop = true,
  cursor = true,
}) {
  const [display, setDisplay] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!phrases.length) return;

    const currentPhrase = phrases[phraseIndex % phrases.length];

    // Pause after full typing
    if (isPaused) {
      const pauseTimer = setTimeout(() => {
        setIsPaused(false);
        setIsDeleting(true);
      }, pauseMs);
      return () => clearTimeout(pauseTimer);
    }

    // Typing
    if (!isDeleting && display.length < currentPhrase.length) {
      const timer = setTimeout(() => {
        setDisplay(currentPhrase.slice(0, display.length + 1));
      }, speed);
      return () => clearTimeout(timer);
    }

    // Finished typing → pause
    if (!isDeleting && display.length === currentPhrase.length) {
      if (!loop && phraseIndex === phrases.length - 1) return;
      setIsPaused(true);
      return;
    }

    // Deleting
    if (isDeleting && display.length > 0) {
      const timer = setTimeout(() => {
        setDisplay(currentPhrase.slice(0, display.length - 1));
      }, deleteSpeed);
      return () => clearTimeout(timer);
    }

    // Finished deleting → next phrase
    if (isDeleting && display.length === 0) {
      setIsDeleting(false);
      setPhraseIndex((i) => (i + 1) % phrases.length);
    }
  }, [display, isDeleting, isPaused, phraseIndex, phrases, speed, pauseMs, deleteSpeed, loop]);

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Box component="span">{display}</Box>
      {cursor && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: '2px',
            height: '1em',
            bgcolor: 'primary.main',
            ml: '2px',
            animation: 'blinkDot 1s ease-in-out infinite',
            verticalAlign: 'middle',
          }}
        />
      )}
    </Box>
  );
});

const AnimatedCounter = memo(function AnimatedCounter({ value, duration = 1500, suffix = '' }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !startedRef.current) {
          startedRef.current = true;
          const start = performance.now();
          const numeric = parseFloat(value);
          const isFloat = value.toString().includes('.');

          const tick = (now) => {
            const progress = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 4);
            const current = numeric * eased;
            setDisplay(isFloat ? current.toFixed(1) : Math.floor(current));
            if (progress < 1) requestAnimationFrame(tick);
            else setDisplay(isFloat ? numeric.toFixed(1) : numeric);
          };
          requestAnimationFrame(tick);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [value, duration]);

  return (
    <Box component="span" ref={ref}>
      {display}
      {suffix}
    </Box>
  );
});

/* ================================================================== */
/*                       MESH BACKGROUND                               */
/* ================================================================== */

const MeshBackground = memo(function MeshBackground() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {/* Base gradient */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: isDark
            ? 'radial-gradient(ellipse 80% 60% at 50% -20%, rgba(99,102,241,0.15) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 80% 100%, rgba(245,158,11,0.08) 0%, transparent 60%), #020617'
            : 'radial-gradient(ellipse 80% 60% at 50% -20%, rgba(99,102,241,0.12) 0%, transparent 60%), radial-gradient(ellipse 60% 50% at 80% 100%, rgba(245,158,11,0.08) 0%, transparent 60%), #fafaf9',
        }}
      />
      {/* Animated mesh blobs */}
      <Box
        className="anim-mesh"
        sx={{
          position: 'absolute',
          width: '60vw',
          height: '60vw',
          top: '-20vw',
          left: '-10vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 65%)',
          filter: 'blur(80px)',
        }}
      />
      <Box
        className="anim-mesh"
        sx={{
          position: 'absolute',
          width: '50vw',
          height: '50vw',
          bottom: '-15vw',
          right: '-10vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,158,11,0.28) 0%, transparent 65%)',
          filter: 'blur(80px)',
          animationDelay: '5s',
        }}
      />
      <Box
        className="anim-mesh"
        sx={{
          position: 'absolute',
          width: '40vw',
          height: '40vw',
          top: '40%',
          left: '30%',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(236,72,153,0.2) 0%, transparent 65%)',
          filter: 'blur(80px)',
          animationDelay: '10s',
        }}
      />
      {/* Grid overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: isDark
            ? 'linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)'
            : 'linear-gradient(rgba(15,23,42,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.04) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage:
            'radial-gradient(ellipse 100% 70% at 50% 40%, black 30%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 100% 70% at 50% 40%, black 30%, transparent 100%)',
        }}
      />
      <Box className="noise-overlay" />
    </Box>
  );
});

/* ================================================================== */
/*                          STATIC DATA                                */
/* ================================================================== */

const studentCards = [
  {
    icon: <CreditCardIcon />,
    title: 'Student ID Card',
    tag: 'Bulk print',
    color: '#6366f1',
    features: [
      'Dynamic template: photo, class, blood group', //barcode/QR',
      'Bulk generation for sections & classes',
      'QR for instant verification & parent contact',
    ],
  },
  {
    icon: <AssignmentIcon />,
    title: 'Marksheet',
    tag: 'CGPA/Percentage',
    color: '#ec4899',
    features: [
      'Term-wise, cumulative, rank & grade analysis',
      'Subject-wise analytics & performance trends',
      'Bulk report card generation',
    ],
  },
  {
    icon: <ReceiptIcon />,
    title: 'Fees Slip',
    tag: null,
    color: '#f59e0b',
    features: [
      'Automated slip with due date & late fine',
      'Real-time ledger: paid / partial / unpaid',
      'Bulk fee receipts & payment gateway sync',
    ],
  },
  {
    icon: <TransferIcon />,
    title: 'Transfer Certificate',
    tag: null,
    color: '#10b981',
    features: [
      'Board-compliant format (CBSE/ICSE/State)',
      'One-click generation locks student status',
      'Digital signature & seal support',
    ],
  },
  {
    icon: <VerifiedIcon />,
    title: 'Bonafide & Character',
    tag: null,
    color: '#06b6d4',
    features: [
      'Instant bonafide for scholarships, passport',
      'Character certificate with discipline log',
      'Self-service download for parents/students',
    ],
  },
  // ✅ NEW MODULES
  {
    icon: <QuizIcon />,
    title: 'Question Paper Generator',
    tag: 'AI-powered',
    color: '#8b5cf6',
    features: [
      'Generate papers from question bank by chapter / difficulty',
      'Bloom\'s taxonomy & CBSE blueprint alignment',
      'Randomized sets A/B/C/D to prevent cheating',
    ],
  },
  {
    icon: <TestSeriesIcon />,
    title: 'Test Series & Mock Exams',
    tag: 'Online',
    color: '#ef4444',
    features: [
      'Chapter-wise, full-syllabus & previous-year series',
      'Auto-evaluation + instant scorecard with rank',
      'Timer-based online tests & printable OMR sheets',
    ],
  },
  {
    icon: <BulkStudentIcon />,
    title: 'Bulk Student Import',
    tag: 'Excel/CSV',
    color: '#14b8a6',
    features: [
      'Upload CSV/XLSX → auto-create student profiles',
      'Auto-generate admission numbers, IDs & login credentials',
      'Parent credentials emailed + SMS on import',
    ],
  },
];

const teacherCards = [
  {
    icon: <TeacherBadgeIcon />,
    title: 'Teacher ID Card',
    tag: 'QR enabled',
    color: '#8b5cf6',
    features: [
      'Photo, designation, department, employee code',
      'QR scanning → instant profile & status',
      'Bulk print for staff',
    ],
  },
  {
    icon: <CertificateIcon />,
    title: 'Experience Certificate',
    tag: 'QR verifiable',
    color: '#f43f5e',
    features: [
      'Tamper-proof PDF with unique hash & QR',
      'Future employer scans to verify tenure',
      'Stored in central ledger for authenticity',
    ],
  },
  {
    icon: <SalaryIcon />,
    title: 'Salary Slip · Monthly',
    tag: null,
    color: '#22c55e',
    features: [
      'Auto payroll with HRA, allowances, TDS',
      'Teachers login → view/download own slips',
      'Bulk slip generation for accounts dept',
    ],
  },
  {
    icon: <EmailIcon />,
    title: 'Offer Letter',
    tag: null,
    color: '#3b82f6',
    features: [
      'Merge fields: name, salary, role, joining date',
      'Digital signature ready, printable letters',
      'Version tracking & HR approval flow',
    ],
  },
  {
    icon: <QrScannerIcon />,
    title: 'Attendance (QR)',
    tag: null,
    color: '#14b8a6',
    features: [
      'Teachers mark presence via dynamic QR',
      'Geo-fencing & real-time dashboard',
      'Late arrival alerts & monthly summary',
    ],
  },
  {
    icon: <ExcelIcon />,
    title: 'Bulk Teacher Import',
    tag: null,
    color: '#0ea5e9',
    features: [
      'Upload CSV/XLSX → auto-create profiles',
      'Auto email with credentials',
      'Reduces manual entry time by 90%',
    ],
  },
];

const classMgmtCards = [
  {
    icon: <CalendarIcon />,
    title: 'Timetable',
    color: '#a855f7',
    features: ['Auto-scheduler (no conflicts)', 'Substitute teacher suggestion'],
  },
  {
    icon: <LessonIcon />,
    title: 'Lesson Plans',
    color: '#f97316',
    features: ['Unit planner with progress tracking', 'Upload resources & assignments'],
  },
  {
    icon: <ExamIcon />,
    title: 'Exam Management',
    color: '#ef4444',
    features: ['Seating arrangement generator', 'Marks entry with grade locking'],
  },
  {
    icon: <ChatIcon />,
    title: 'Communication',
    color: '#0891b2',
    features: ['PTM scheduler', 'Broadcast announcements'],
  },
];

const rbacData = [
  {
    role: 'Admin',
    studentDocs: 'Full access',
    teacherFinancials: 'Salary slips, all fees',
    certificates: 'Generate/Revoke any',
    attendanceBulk: 'Full import, config',
    studentIcon: <CheckIcon color="success" fontSize="small" />,
    teacherIcon: <CheckIcon color="success" fontSize="small" />,
    certIcon: <CheckIcon color="success" fontSize="small" />,
    bulkIcon: <CheckIcon color="success" fontSize="small" />,
  },
  {
    role: 'Principal',
    studentDocs: 'View/Generate all',
    teacherFinancials: 'No salary edit',
    certificates: 'TC, Exp certs',
    attendanceBulk: 'View staff attendance',
    studentIcon: <CheckIcon color="success" fontSize="small" />,
    teacherIcon: <CloseIcon color="error" fontSize="small" />,
    certIcon: <CheckIcon color="success" fontSize="small" />,
    bulkIcon: <CheckIcon color="success" fontSize="small" />,
  },
  {
    role: 'Accountant',
    studentDocs: 'Fee slips only',
    teacherFinancials: 'Salary slip & payroll',
    certificates: 'No TC/Experience',
    attendanceBulk: 'No bulk teacher import',
    studentIcon: <PartialIcon color="warning" fontSize="small" />,
    teacherIcon: <CheckIcon color="success" fontSize="small" />,
    certIcon: <CloseIcon color="error" fontSize="small" />,
    bulkIcon: <CloseIcon color="error" fontSize="small" />,
  },
  {
    role: 'Class Teacher',
    studentDocs: 'Own class only',
    teacherFinancials: 'No salary access',
    certificates: 'Character cert (own class)',
    attendanceBulk: 'Class attendance',
    studentIcon: <PartialIcon color="warning" fontSize="small" />,
    teacherIcon: <CloseIcon color="error" fontSize="small" />,
    certIcon: <PartialIcon color="warning" fontSize="small" />,
    bulkIcon: <CheckIcon color="success" fontSize="small" />,
  },
  {
    role: 'Teacher (self)',
    studentDocs: '—',
    teacherFinancials: 'Own salary slip',
    certificates: 'Own experience cert',
    attendanceBulk: 'Self QR attendance',
    studentIcon: <CloseIcon color="error" fontSize="small" />,
    teacherIcon: <CheckIcon color="success" fontSize="small" />,
    certIcon: <CheckIcon color="success" fontSize="small" />,
    bulkIcon: <CheckIcon color="success" fontSize="small" />,
  },
  {
    role: 'Student/Parent',
    studentDocs: 'View ID, marksheet, fees',
    teacherFinancials: '—',
    certificates: 'Download Bonafide/TC',
    attendanceBulk: '—',
    studentIcon: <CheckIcon color="success" fontSize="small" />,
    teacherIcon: <CloseIcon color="error" fontSize="small" />,
    certIcon: <PartialIcon color="warning" fontSize="small" />,
    bulkIcon: <CloseIcon color="error" fontSize="small" />,
  },
];

/* ================================================================== */
/*                    COMMAND PALETTE                                  */
/* ================================================================== */

const CommandPalette = memo(function CommandPalette({ open, onClose, onNavigate }) {
  const theme = useTheme();
  const [query, setQuery] = useState('');

  const commands = [
    { id: 0, label: 'Go to Dashboard', icon: <SchoolIcon />, group: 'Navigation' },
    { id: 1, label: 'Create Offer Letter', icon: <AssignmentIcon />, group: 'Tools' },
    { id: 'pdf', label: 'Open PDF Converter', icon: <PictureAsPdf />, group: 'Tools' },
    { id: 3, label: 'Check Exam Result', icon: <SchoolIcon />, group: 'Tools' },
    { id: 2, label: 'About JMK ERP', icon: <Info />, group: 'Info' },
    { id: 'theme', label: 'Toggle Theme', icon: <MoonIcon />, group: 'Preferences' },
  ];

  const filtered = query
    ? commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase()))
    : commands;

  const grouped = filtered.reduce((acc, cmd) => {
    if (!acc[cmd.group]) acc[cmd.group] = [];
    acc[cmd.group].push(cmd);
    return acc;
  }, {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Zoom}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(30px)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: '0 40px 80px -20px rgba(0,0,0,0.5)',
          mt: -8,
        },
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <TextField
          autoFocus
          fullWidth
          placeholder="Type a command or search..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          variant="standard"
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            sx: { fontSize: '1rem', fontWeight: 500 },
          }}
        />
      </Box>
      <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 1 }}>
        {Object.entries(grouped).map(([group, cmds]) => (
          <Box key={group} sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                fontSize: '0.68rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              {group}
            </Typography>
            {cmds.map((cmd) => (
              <Box
                key={cmd.id}
                onClick={() => {
                  onNavigate(cmd.id);
                  onClose();
                  setQuery('');
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& svg': { fontSize: 18 },
                  }}
                >
                  {cmd.icon}
                </Box>
                <Typography fontSize="0.9rem" fontWeight={600}>
                  {cmd.label}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
        {filtered.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" fontSize="0.9rem">
              No commands found
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
});

/* ================================================================== */
/*                     FLOATING TOP NAV                                */
/* ================================================================== */

const FloatingTopNav = memo(function FloatingTopNav({
  authTab,
  setAuthTab,
  onLoginClick,
  isAuthenticated,
  user,
  onLogout,
  toggleColorMode,
  onOpenCommand,
  onOpenPdf,
  mode,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenCommand();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onOpenCommand]);

  const navItems = [
    { id: 0, label: 'Home' },
    { id: 1, label: 'Offer Letter' },
    { id: 'pdf', label: 'PDF Tool' },
    { id: 3, label: 'Results' },
    { id: 2, label: 'About' },
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        p: { xs: 1.5, md: 2 },
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Container maxWidth="xl" disableGutters>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            px: { xs: 1.5, md: 2.5 },
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, md: 2 },
            bgcolor: scrolled
              ? alpha(theme.palette.background.paper, isDark ? 0.85 : 0.9)
              : alpha(theme.palette.background.paper, isDark ? 0.6 : 0.7),
            backdropFilter: 'blur(24px) saturate(180%)',
            border: '1px solid',
            borderColor: scrolled
              ? alpha(theme.palette.divider, 0.9)
              : alpha(theme.palette.divider, 0.5),
            boxShadow: scrolled
              ? isDark
                ? '0 16px 40px -12px rgba(0,0,0,0.5)'
                : '0 16px 40px -12px rgba(0,0,0,0.12)'
              : 'none',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Logo */}
          <Box
            onClick={() => setAuthTab(0)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'transform 0.3s',
              '&:hover': { transform: 'scale(1.02)' },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  inset: -4,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(99,102,241,0.3) 0%, transparent 70%)',
                  opacity: 0,
                  transition: 'opacity 0.3s',
                  zIndex: -1,
                },
                '&:hover::after': { opacity: 1 },
              }}
            >
              <img
                src={isDark ? LogoDark : Logo}
                alt="jmk"
                width={isMobile ? 36 : 42}
              />
            </Box>
            {!isMobile && (
              <Box>
                <Typography fontWeight={900} fontSize="0.95rem" letterSpacing="-0.02em" lineHeight={1}>
                  JMKInfo
                </Typography>
                <Typography
                  fontSize="0.62rem"
                  color="text.secondary"
                  letterSpacing="0.15em"
                  fontWeight={600}
                  lineHeight={1.2}
                >
                  SCHOOL ERP
                </Typography>
              </Box>
            )}
          </Box>

          {/* Divider */}
          {/* {!isMobile && (
            <Box sx={{ width: 1, height: 24, bgcolor: 'divider', opacity: 0.6 }} />
          )} */}

          {/* Nav items - desktop */}
          {!isMobile && (
            <Stack direction="row" spacing={0.5} sx={{ flex: 1, justifyContent: 'flex-end' }}>
              {navItems.map((item) => {
                const isActive = authTab === item.id;
                return (
                  <Button
                    key={item.id}
                    onClick={() => {
                      if (item.id === 'pdf') onOpenPdf();
                      else setAuthTab(item.id);
                    }}
                    sx={{
                      borderRadius: 2,
                      px: 1.75,
                      py: 0.75,
                      minWidth: 'auto',
                      fontSize: '0.85rem',
                      fontWeight: isActive ? 700 : 600,
                      color: isActive ? 'primary.main' : 'text.secondary',
                      bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                      transition: 'all 0.2s',
                      '&:hover': {
                        color: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.06),
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          )}

          {isMobile && <Box sx={{ flex: 1 }} />}

          {/* Command palette trigger */}
          {/* {!isMobile && (
            <Button
              onClick={onOpenCommand}
              startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2.5,
                px: 1.5,
                py: 0.75,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'text.secondary',
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.7),
                '&:hover': {
                  bgcolor: alpha(theme.palette.text.primary, 0.06),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                },
              }}
            >
              Search
            </Button>
          )} */}

          {/* Theme toggle */}
          <Tooltip title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <IconButton
              onClick={toggleColorMode}
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.7),
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  '& svg': { transform: 'rotate(180deg)' },
                },
                '& svg': { transition: 'transform 0.5s', fontSize: 18 },
              }}
            >
              {mode === 'dark' ? <SunIcon /> : <MoonIcon />}
            </IconButton>
          </Tooltip>

          {/* Auth buttons */}
          {!isAuthenticated ? (
            <Button
              onClick={onLoginClick}
              variant="contained"
              endIcon={!isMobile && <LoginIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: 2.5,
                px: { xs: 1.75, md: 2.25 },
                py: 0.9,
                fontSize: '0.82rem',
                fontWeight: 800,
                textTransform: 'none',
                bgcolor: 'text.primary',
                color: 'background.paper',
                transition: 'all 0.3s',
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: '#fff',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 12px 24px -8px rgba(99,102,241,0.5)',
                },
              }}
            >
              {isMobile ? 'Sign in' : 'Get started'}
            </Button>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip title={user?.name}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'primary.main',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    border: '2px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  }}
                >
                  {user?.name?.charAt(0).toUpperCase()}
                </Avatar>
              </Tooltip>
              <Tooltip title="Logout">
                <IconButton
                  onClick={onLogout}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.7),
                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                  }}
                >
                  <LogoutIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          )}

          {/* Mobile menu button */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.7),
              }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </Paper>
      </Container>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            bgcolor: alpha(theme.palette.background.paper, 0.98),
            backdropFilter: 'blur(30px)',
            p: 2,
          },
        }}
      >
        <Stack spacing={1} sx={{ mt: 2 }}>
          {navItems.map((item) => (
            <Button
              key={item.id}
              onClick={() => {
                if (item.id === 'pdf') onOpenPdf();
                else setAuthTab(item.id);
                setMobileMenuOpen(false);
              }}
              sx={{
                justifyContent: 'flex-start',
                borderRadius: 2,
                py: 1.5,
                px: 2,
                fontSize: '0.9rem',
                fontWeight: 600,
                color: authTab === item.id ? 'primary.main' : 'text.primary',
                bgcolor: authTab === item.id ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
      </Drawer>
    </Box>
  );
});

/* ================================================================== */
/*                       MAGAZINE HERO                                 */
/* ================================================================== */

const MagazineHero = memo(function MagazineHero({ onGetStarted }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const stats = [
    { value: '90', suffix: '%', label: 'Faster workflow', color: '#6366f1' },
    { value: '256', suffix: 'b', label: 'Security bits', color: '#8b5cf6' },
    { value: '500', suffix: '+', label: 'Schools onboard', color: '#ec4899' },
    { value: '1', suffix: 's', label: 'Bulk generate', color: '#f59e0b' },
  ];

  const quickFeatures = [
    { icon: <QrCodeIcon />, title: 'QR Certificates', desc: 'Blockchain-verified', color: '#6366f1' },
    { icon: <BoltIcon />, title: 'Bulk Ops', desc: '500+ in a click', color: '#f59e0b' },
    { icon: <ShieldIcon />, title: 'RBAC', desc: 'Granular control', color: '#10b981' },
    { icon: <AutoGraphIcon />, title: 'Analytics', desc: 'Real-time insights', color: '#ec4899' },
  ];

  return (
    <Box
      className="anim-fade-up"
      sx={{
        position: 'relative',
        pt: { xs: 14, md: 16 },
        pb: { xs: 6, md: 10 },
      }}
    >
      <Container maxWidth="xl" disableGutters>
        <Grid container spacing={4} alignItems="center" justifyContent={'space-around'}>
          {/* Left Column - Content */}
          <Grid item xs={12} lg={7}>
            {/* Eyebrow */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                mb: 3,
                p: 0.75,
                pr: 2,
                width: 'fit-content',
                borderRadius: 4,
                bgcolor: alpha(theme.palette.primary.main, 0.06),
                border: '1px solid',
                borderColor: alpha(theme.palette.primary.main, 0.15),
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& svg': { fontSize: 14 },
                }}
              >
                <SparkleIcon />
              </Box>
              <Typography
                fontSize="0.75rem"
                fontWeight={700}
                color="primary.main"
                letterSpacing="0.02em"
              >
                v3.0 · AI-Powered School Platform
              </Typography>
              <Chip
                label="NEW"
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.6rem',
                  fontWeight: 900,
                  bgcolor: 'secondary.main',
                  color: '#fff',
                  ml: 0.5,
                }}
              />
            </Stack>

            {/* Headline */}
            <Typography
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2.75rem', sm: '3.75rem', md: '4.75rem', lg: '5.25rem' },
                lineHeight: 0.98,
                letterSpacing: '-0.05em',
                mb: 2.5,
              }}
            >
              Your school,
              <br />
              <Box
                component="span"
                className="anim-gradient-shift"
                sx={{
                  marginLeft: 15,
                  background: 'linear-gradient(90deg, #6366f1 0%, #ec4899 50%, #f59e0b 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontStyle: 'italic',
                }}
              >
                reimagined.
              </Box>
            </Typography>

            {/* Subtitle */}
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                lineHeight: 1.6,
                color: 'text.secondary',
                maxWidth: 560,
                mb: 4,
                fontWeight: 400,
              }}
            >
              A complete school management platform for admissions, attendance, examinations, fee collection, staff management, ID cards, marksheets, QR-verified certificates, salary slips, Question-paper, Test-Series and a dozen more tools. All in one unified platform.
            </Typography>

            {/* CTA buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 5 }}>
              <Button
                onClick={onGetStarted}
                variant="contained"
                size="large"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  // py: 0,
                  fontWeight: 800,
                  fontSize: '0.95rem',
                  bgcolor: 'text.primary',
                  color: 'background.paper',
                  transition: 'all 0.3s',
                  boxShadow: '0 12px 24px -8px rgba(0,0,0,0.3)',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 20px 40px -8px rgba(99,102,241,0.5)',
                  },
                }}
              >
                Start free trial
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<PlayIcon sx={{ fontSize: 20 }} />}
                sx={{
                  borderRadius: 3,
                  px: 3.5,
                  py: 1.6,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: 'text.primary',
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  borderWidth: 1.5,
                  '&:hover': {
                    borderColor: 'text.primary',
                    borderWidth: 1.5,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                  },
                }}
              >
                Watch demo
              </Button>
            </Stack>

            {/* Social proof */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row">
                {['#6366f1', '#ec4899', '#f59e0b', '#10b981'].map((c, i) => (
                  <Avatar
                    key={i}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: c,
                      border: '2px solid',
                      borderColor: 'background.default',
                      ml: i > 0 ? -1 : 0,
                      fontSize: '0.7rem',
                      fontWeight: 800,
                    }}
                  >
                    {['D', 'R', 'K', 'S'][i]}
                  </Avatar>
                ))}
              </Stack>
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <StarIcon key={s} sx={{ fontSize: 14, color: '#f59e0b' }} />
                  ))}
                  <Typography fontSize="0.8rem" fontWeight={800} sx={{ ml: 0.5 }}>
                    4.9
                  </Typography>
                </Stack>
                <Typography fontSize="0.72rem" color="text.secondary">
                  Trusted by 500+ schools
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Right Column - Visual */}
          <Grid item xs={12} lg={5}>
            <Box sx={{ position: 'relative', pl: { xs: 0, lg: 3 } }}>
              {/* Main Card — Question Paper / Test Series */}
              <Box
                className="anim-float-soft"
                sx={{
                  position: 'relative',
                  borderRadius: 4,
                  p: 3,
                  bgcolor: isDark ? 'rgba(15,23,42,0.85)' : 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(30px)',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: isDark
                    ? '0 40px 80px -30px rgba(0,0,0,0.6)'
                    : '0 40px 80px -30px rgba(0,0,0,0.2)',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    background: 'linear-gradient(90deg, #6366f1, #ec4899, #f59e0b, #6366f1)',
                    backgroundSize: '300% 100%',
                    animation: 'gradientShift 4s ease infinite',
                  },
                }}
              >
                {/* Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      fontSize="0.65rem"
                      fontWeight={800}
                      letterSpacing="0.1em"
                      color="text.secondary"
                    >
                      INSTANT GENERATION
                    </Typography>
                    <Typography fontSize="0.95rem" fontWeight={800} mt={0.25}>
                      <TypingText
                        phrases={[
                          'Question Paper · Class X',
                          'Test Series · Physics',
                          'Question Paper · Class XII',
                          'Mock Test · Mathematics',
                          'Question Paper · Class IX',
                          'Test Series · Chemistry',
                        ]}
                        speed={65}
                        pauseMs={1400}
                      />
                    </Typography>
                  </Box>
                  <Chip
                    icon={
                      <Box
                        className="anim-blink-dot"
                        sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', ml: 1 }}
                      />
                    }
                    label="Live"
                    size="small"
                    sx={{
                      bgcolor: alpha('#22c55e', 0.12),
                      color: '#16a34a',
                      fontWeight: 800,
                      fontSize: '0.68rem',
                      height: 22,
                      flexShrink: 0,
                    }}
                  />
                </Stack>

                {/* QR + Info */}
                <Stack direction="row" spacing={2.5} alignItems="center" mb={2.5}>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography fontSize="0.95rem" fontWeight={800} letterSpacing="-0.01em">
                      <TypingText
                        phrases={[
                          'Mathematics · CBSE Pattern',
                          'Physics · JEE Mock Test',
                          'Science · NCERT Aligned',
                          'Maths · Olympiad Level',
                          'English · Board Pattern',
                          'Chemistry · NEET Mock',
                        ]}
                        speed={70}
                        pauseMs={1500}
                      />
                    </Typography>
                    <Typography fontSize="0.72rem" color="text.secondary" sx={{ mb: 1 }}>
                      <TypingText
                        phrases={[
                          'Chapter 5 · 20 MCQs · Set A',
                          'Chapters 1-4 · 30 Questions',
                          'Full Syllabus · 3 hours',
                          'Algebra · 25 Problems',
                          'Literature · 15 Questions',
                          'Organic Chemistry · 40 MCQs',
                        ]}
                        speed={45}
                        pauseMs={1300}
                      />
                    </Typography>
                    {/* <Stack direction="row" alignItems="center" spacing={0.75}>
                      <CheckIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                      <Typography fontSize="0.72rem" fontWeight={700} color="#16a34a">
                        Auto-generated
                      </Typography>
                    </Stack> */}
                  </Box>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Stack direction="row" justifyContent="space-between">
                  {[
                    { label: 'Sets', value: 'A/B/C/D' },
                    { label: 'Marks', value: '80' },
                    { label: 'Time', value: '3 hrs' },
                  ].map((item, i) => (
                    <Box key={i}>
                      <Typography
                        fontSize="0.6rem"
                        fontWeight={800}
                        letterSpacing="0.1em"
                        color="text.secondary"
                      >
                        {item.label.toUpperCase()}
                      </Typography>
                      <Typography fontSize="0.78rem" fontWeight={700} mt={0.25}>
                        {item.value}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>

              {/* Floating mini card — top left: Question Bank */}
              <Box
                className="anim-float-soft"
                sx={{
                  position: 'absolute',
                  top: -50,
                  left: { xs: -10, lg: -30 },
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: isDark ? 'rgba(15,23,42,0.9)' : '#fff',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                  animationDelay: '1s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  minWidth: 150,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha('#6366f1', 0.15),
                    color: '#6366f1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <QuizIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize="0.85rem" fontWeight={900} lineHeight={1}>
                    <AnimatedCounter value="1000" suffix="+" />
                  </Typography>
                  <Typography fontSize="0.62rem" color="text.secondary" fontWeight={600}>
                    Questions in bank
                  </Typography>
                </Box>
              </Box>

              {/* Floating mini card — bottom right: Test Series */}
              <Box
                className="anim-float-soft"
                sx={{
                  position: 'absolute',
                  bottom: -30,
                  right: { xs: 0, lg: -30 },
                  p: 1.75,
                  borderRadius: 2.5,
                  bgcolor: isDark ? 'rgba(15,23,42,0.9)' : '#fff',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 20px 40px -12px rgba(0,0,0,0.2)',
                  animationDelay: '2.5s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  minWidth: 150,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    bgcolor: alpha('#22c55e', 0.15),
                    color: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <TestSeriesIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize="0.85rem" fontWeight={900} lineHeight={1}>
                    <AnimatedCounter value="500" suffix="+" />
                  </Typography>
                  <Typography fontSize="0.62rem" color="text.secondary" fontWeight={600}>
                    Test series ready
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Stats strip */}
        <Box
          sx={{
            mt: { xs: 8, md: 10 },
            py: 3,
            px: { xs: 2, md: 4 },
            borderRadius: 4,
            bgcolor: alpha(theme.palette.background.paper, 0.5),
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: alpha(theme.palette.divider, 0.6),
          }}
        >
          <Grid container spacing={2}>
            {stats.map((stat, idx) => (
              <Grid item xs={6} md={3} key={idx}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: alpha(stat.color, 0.06),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 40,
                      borderRadius: 1,
                      bgcolor: stat.color,
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        lineHeight: 1,
                        letterSpacing: '-0.04em',
                        color: stat.color,
                      }}
                    >
                      <AnimatedCounter value={stat.value} />
                      {stat.suffix}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '0.7rem',
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: '0.05em',
                        mt: 0.5,
                        textTransform: 'uppercase',
                      }}
                    >
                      {stat.label}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Quick features row */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          {quickFeatures.map((f, idx) => (
            <Grid item xs={6} md={3} key={idx}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: alpha(theme.palette.divider, 0.5),
                  bgcolor: alpha(theme.palette.background.paper, 0.4),
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.3s',
                  cursor: 'default',
                  '&:hover': {
                    borderColor: alpha(f.color, 0.4),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 24px -12px ${f.color}60`,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(f.color, 0.12),
                    color: f.color,
                    flexShrink: 0,
                    '& svg': { fontSize: 20 },
                  }}
                >
                  {f.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography fontSize="0.82rem" fontWeight={800} lineHeight={1.2} noWrap>
                    {f.title}
                  </Typography>
                  <Typography fontSize="0.7rem" color="text.secondary" noWrap>
                    {f.desc}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
});

/* ================================================================== */
/*                       FEATURE LIST                                  */
/* ================================================================== */

const FeatureList = memo(function FeatureList({ features, color = '#6366f1' }) {
  return (
    <Stack spacing={0.75}>
      {features.map((feature, idx) => (
        <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              bgcolor: alpha(color, 0.15),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mt: 0.4,
              flexShrink: 0,
            }}
          >
            <CheckIcon sx={{ fontSize: 9, color }} />
          </Box>
          <Typography
            sx={{
              fontSize: '0.8rem',
              lineHeight: 1.5,
              color: 'text.secondary',
              fontWeight: 500,
            }}
          >
            {feature}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
});

/* ================================================================== */
/*                    FEATURE CARD (REFINED)                           */
/* ================================================================== */

const FeatureCard = memo(function FeatureCard({
  icon,
  title,
  tag,
  features,
  color = '#6366f1',
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    card.style.setProperty('--mouse-x', `${x}%`);
    card.style.setProperty('--mouse-y', `${y}%`);
  }, []);

  return (
    <Card
      ref={cardRef}
      onMouseMove={handleMouseMove}
      sx={{
        '--mouse-x': '50%',
        '--mouse-y': '50%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.6),
        bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
        backdropFilter: 'blur(20px)',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(400px circle at var(--mouse-x) var(--mouse-y), ${alpha(
            color,
            0.08
          )}, transparent 40%)`,
          opacity: 0,
          transition: 'opacity 0.4s',
          pointerEvents: 'none',
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha(color, 0.35),
          boxShadow: `0 24px 48px -16px ${alpha(color, 0.25)}`,
          '&::before': { opacity: 1 },
          '& .icon-box': {
            bgcolor: alpha(color, 0.15),
            color: color,
            transform: 'scale(1.05)',
          },
          '& .title-text': { color },
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1.75} alignItems="center" mb={2}>
          <Box
            className="icon-box"
            sx={{
              width: 46,
              height: 46,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isDark ? alpha('#334155', 0.4) : alpha('#f1f5f9', 1),
              color: 'text.secondary',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              flexShrink: 0,
              '& svg': { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography
                className="title-text"
                variant="h6"
                component="h3"
                fontWeight={800}
                sx={{
                  fontSize: '1rem',
                  letterSpacing: '-0.015em',
                  lineHeight: 1.2,
                  transition: 'color 0.3s',
                }}
              >
                {title}
              </Typography>
              {tag && (
                <Box
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 800,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color,
                    bgcolor: alpha(color, 0.1),
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.75,
                  }}
                >
                  {tag}
                </Box>
              )}
            </Stack>
          </Box>
        </Stack>
        <FeatureList features={features} color={color} />
      </CardContent>
    </Card>
  );
});

/* ================================================================== */
/*                       SECTION TITLE (NEW)                           */
/* ================================================================== */

const SectionTitle = memo(function SectionTitle({ title, subtitle, badge, icon }) {
  return (
    <Box sx={{ mb: 4, mt: 9 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: 900,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'primary.main',
          }}
        >
          {badge}
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider', position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: -1,
              width: 40,
              height: 3,
              borderRadius: 1.5,
              bgcolor: 'primary.main',
            }}
          />
        </Box>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 1, md: 2 }} alignItems={{ md: 'flex-end' }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 900,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            letterSpacing: '-0.04em',
            lineHeight: 1.1,
            maxWidth: 620,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: { xs: '0.9rem', md: '1rem' },
              color: 'text.secondary',
              lineHeight: 1.5,
              maxWidth: 480,
              fontWeight: 400,
              pb: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Stack>
    </Box>
  );
});

/* ================================================================== */
/*                            QR DEMO                                  */
/* ================================================================== */

const QrDemo = memo(function QrDemo() {
  const [verified, setVerified] = useState(false);
  const [scanning, setScanning] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleScan = () => {
    setScanning(true);
    setTimeout(() => {
      setScanning(false);
      setVerified(true);
    }, 1500);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
        borderRadius: 4,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.7),
        bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
        backdropFilter: 'blur(20px)',
      }}
    >
      {/* Left - QR Visual */}
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          bgcolor: isDark ? alpha('#020617', 0.6) : alpha('#f8fafc', 1),
          borderRight: { md: '1px solid' },
          borderBottom: { xs: '1px solid', md: 'none' },
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        <Stack direction="row" spacing={2.5} alignItems="center" mb={2.5}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: 1,
              bgcolor: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid #e2e8f0',
              boxShadow: '0 8px 24px -8px rgba(0,0,0,0.15)',
              flexShrink: 0,
            }}
          >
            <QrCodeIcon sx={{ fontSize: 100, color: verified ? '#22c55e' : '#0f172a' }} />
            {!verified && (
              <Box
                className="anim-scan-sweep"
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  height: 2,
                  background: 'linear-gradient(90deg, transparent, #6366f1, transparent)',
                  boxShadow: '0 0 16px 3px #6366f1',
                }}
              />
            )}
          </Box>

        </Stack>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 900,
              letterSpacing: '0.2em',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            CERTIFICATE ID
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, letterSpacing: '0.05em' }}>
            VERIFY-EXP-101
          </Typography>
        </Box>
      </Box>

      {/* Right - Details */}
      <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, #f59e0b 0%, #ec4899 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TeacherBadgeIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography fontSize="0.68rem" fontWeight={800} letterSpacing="0.1em" color="text.secondary">
                TEACHER · MATH DEPT
              </Typography>
              <Typography variant="h6" fontWeight={900} fontSize="1.05rem" letterSpacing="-0.01em" lineHeight={1.2}>
                Brijsen Khutiyare
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ mb: 1 }} />

          <Stack spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Tenure', value: '2019 - 2024 (15 years)' },
              { label: 'Position', value: 'Senior Mathematics Faculty' },
              { label: 'Blockchain Hash', value: '0x7f3e9a2b8c4d' },
            ].map((item, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ marginTop: 1 }}>
                <Typography fontSize="0.75rem" color="text.secondary" fontWeight={600}>
                  {item.label}
                </Typography>
                <Typography fontSize="0.82rem" fontWeight={700}>
                  {item.value}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              bgcolor: verified
                ? alpha('#22c55e', 0.08)
                : scanning
                  ? alpha('#6366f1', 0.08)
                  : alpha(theme.palette.text.primary, 0.03),
              border: '1px solid',
              borderColor: verified
                ? alpha('#22c55e', 0.3)
                : scanning
                  ? alpha('#6366f1', 0.3)
                  : 'divider',
              transition: 'all 0.4s',
            }}
          >
            {verified ? (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography fontSize="0.72rem" fontWeight={900} color="#16a34a" letterSpacing="0.03em">
                    VERIFIED AUTHENTIC
                  </Typography>
                  <Typography fontSize="0.72rem" color="text.secondary">
                    Matches blockchain record
                  </Typography>
                </Box>
              </Stack>
            ) : scanning ? (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  className="anim-spin-slow"
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    border: '2px solid #6366f1',
                    borderTopColor: 'transparent',
                    flexShrink: 0,
                    animationDuration: '1s',
                  }}
                />
                <Typography fontSize="0.78rem" color="text.secondary" fontStyle="italic">
                  Verifying on blockchain...
                </Typography>
              </Stack>
            ) : (
              <Typography fontSize="0.78rem" color="text.secondary" fontStyle="italic">
                Awaiting scan — click the button below
              </Typography>
            )}
          </Box>
        </Box>

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<QrScannerIcon />}
            onClick={handleScan}
            disabled={scanning}
            sx={{
              flex: 1,
              borderRadius: 2.5,
              py: 1.3,
              fontWeight: 800,
              fontSize: '0.85rem',
              bgcolor: 'text.primary',
              color: 'background.paper',
              transition: 'all 0.3s',
              '&:hover': {
                bgcolor: 'primary.main',
                color: '#fff',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 24px -8px rgba(99,102,241,0.5)',
              },
            }}
          >
            {scanning ? 'Scanning...' : 'Simulate Scan'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setVerified(false);
              setScanning(false);
            }}
            sx={{
              borderRadius: 2.5,
              px: 2.5,
              py: 1.3,
              fontWeight: 700,
              fontSize: '0.85rem',
              color: 'text.secondary',
              borderColor: alpha(theme.palette.text.primary, 0.15),
              borderWidth: 1.5,
              '&:hover': { borderColor: 'text.primary', borderWidth: 1.5 },
            }}
          >
            Reset
          </Button>
        </Stack>
      </Box>
    </Box>
  );
});

/* ================================================================== */
/*                          RBAC TABLE                                 */
/* ================================================================== */

const RbacTable = memo(function RbacTable() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: 3,
        overflowX: 'auto',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.7),
        bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
        backdropFilter: 'blur(20px)',
      }}
    >
      <Table sx={{ minWidth: 720 }}>
        <TableHead>
          <TableRow>
            {['Role', 'Student Docs', 'Teacher Financials', 'Certificates', 'Attendance'].map((h) => (
              <TableCell
                key={h}
                sx={{
                  fontWeight: 900,
                  fontSize: '0.65rem',
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  py: 2.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rbacData.map((row, idx) => (
            <TableRow
              key={row.role}
              sx={{
                transition: 'background 0.2s',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                '&:last-child td': { borderBottom: 'none' },
              }}
            >
              <TableCell
                component="th"
                scope="row"
                sx={{ py: 2.25, borderColor: 'divider' }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: `hsl(${idx * 55}, 70%, 55%)`,
                    }}
                  />
                  <Typography fontWeight={800} fontSize="0.88rem">
                    {row.role}
                  </Typography>
                </Stack>
              </TableCell>
              {[row.studentDocs, row.teacherFinancials, row.certificates, row.attendanceBulk].map(
                (text, i) => (
                  <TableCell key={i} sx={{ py: 2.25, borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {[row.studentIcon, row.teacherIcon, row.certIcon, row.bulkIcon][i]}
                      <Typography variant="body2" fontSize="0.8rem" fontWeight={500}>
                        {text}
                      </Typography>
                    </Stack>
                  </TableCell>
                )
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

/* ================================================================== */
/*                     BULK OPERATIONS BANNER                          */
/* ================================================================== */

const BulkOperations = memo(function BulkOperations({ onSuccess }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const items = [
    {
      icon: <ExcelIcon />,
      title: 'Bulk Operations',
      desc: 'Generate 500+ ID cards in one click from an Excel sheet.',
      color: '#10b981',
      onClick: null,
    },
    {
      icon: <ContactMailIcon />,
      title: 'Custom Templates',
      desc: 'Need a bespoke design? Our team builds it for you.',
      color: '#6366f1',
      onClick: onSuccess,
    },
    {
      icon: <PrintIcon />,
      title: 'Multi-format Export',
      desc: 'PDF · CSV · thermal print · A4 · PVC card.',
      color: '#f59e0b',
      onClick: null,
    },
  ];

  return (
    <Box
      sx={{
        borderRadius: 4,
        p: { xs: 2.5, md: 3 },
        mt: 6,
        mb: 4,
        bgcolor: isDark ? alpha('#0f172a', 0.6) : alpha('#ffffff', 0.7),
        backdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.7),
      }}
    >
      <Grid container spacing={2}>
        {items.map((item, idx) => (
          <Grid item xs={12} md={4} key={idx}>
            <Box
              onClick={item.onClick || undefined}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                borderRadius: 3,
                cursor: item.onClick ? 'pointer' : 'default',
                transition: 'all 0.3s',
                '&:hover': item.onClick
                  ? {
                    bgcolor: alpha(item.color, 0.05),
                    transform: 'translateX(4px)',
                  }
                  : {},
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(item.color, 0.12),
                  color: item.color,
                  flexShrink: 0,
                  '& svg': { fontSize: 22 },
                }}
              >
                {item.icon}
              </Box>
              <Box>
                <Typography fontWeight={800} fontSize="0.9rem" gutterBottom lineHeight={1.3}>
                  {item.title}
                </Typography>
                <Typography fontSize="0.75rem" color="text.secondary" lineHeight={1.5}>
                  {item.desc}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
});

/* ================================================================== */
/*                             FOOTER                                  */
/* ================================================================== */

const Footer = memo(function Footer() {
  return (
    <Box
      component="footer"
      sx={{
        py: 5,
        mt: 6,
        borderTop: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            background: 'linear-gradient(135deg, #6366f1, #ec4899)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SchoolIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography fontSize="0.82rem" fontWeight={900} letterSpacing="-0.01em">
            JMKInfo School ERP
          </Typography>
          <Typography fontSize="0.68rem" color="text.secondary">
            © {new Date().getFullYear()} All rights reserved
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap' }}>
        {[
          { icon: <ShieldIcon sx={{ fontSize: 14 }} />, label: 'Secure' },
          { icon: <QrCodeIcon sx={{ fontSize: 14 }} />, label: 'QR Verified' },
          { icon: <LockIcon sx={{ fontSize: 14 }} />, label: 'RBAC' },
          { icon: <CloudDoneIcon sx={{ fontSize: 14 }} />, label: 'Real-time' },
        ].map((item, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{ color: 'primary.main', display: 'flex' }}>{item.icon}</Box>
            <Typography fontSize="0.72rem" color="text.secondary" fontWeight={600}>
              {item.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
});

/* ================================================================== */
/*                            PLAN CARD                                */
/* ================================================================== */

const PlanCard = memo(function PlanCard({ plan }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const isActive = plan.active;
  const discountedPrice = getDiscountedPrice(plan);
  const discountType = plan.couponDTO?.discountType;
  const discountValue =
    discountType === 'PERCENTAGE'
      ? `${plan.couponDTO.discountPercentage}%`
      : discountType === 'FLAT'
        ? formatPrice(plan.couponDTO.discountAmount)
        : null;

  const isPopular = plan.popular;

  const handleLogin = () => {
    setSnackbar({ open: true, message: 'Please log in to proceed.', severity: 'info' });
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          borderRadius: 3,
          opacity: isActive ? 1 : 0.5,
          border: isPopular ? '2px solid' : '1px solid',
          borderColor: isPopular ? 'primary.main' : alpha(theme.palette.divider, 0.7),
          bgcolor: isDark ? alpha('#0f172a', 0.7) : alpha('#ffffff', 0.7),
          backdropFilter: 'blur(20px)',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': isActive
            ? {
              transform: 'translateY(-6px)',
              boxShadow: isPopular
                ? '0 32px 64px -24px rgba(99,102,241,0.4)'
                : '0 32px 64px -24px rgba(0,0,0,0.15)',
            }
            : {},
        }}
      >
        {isPopular && (
          <Box
            sx={{
              position: 'absolute',
              top: -1,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 2,
              py: 0.5,
              borderRadius: '0 0 8px 8px',
              bgcolor: 'primary.main',
              color: '#fff',
              fontSize: '0.6rem',
              fontWeight: 900,
              letterSpacing: '0.15em',
            }}
          >
            MOST POPULAR
          </Box>
        )}
        {!isActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              bgcolor: 'warning.main',
              color: '#fff',
              fontSize: '0.6rem',
              fontWeight: 900,
              letterSpacing: '0.05em',
            }}
          >
            SOON
          </Box>
        )}

        <CardContent sx={{ p: 3.5, pt: 5, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontWeight: 900,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              mb: 2,
            }}
          >
            {plan.name}
          </Typography>

          <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: 0.5 }}>
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: '2rem', md: '2.5rem' },
                letterSpacing: '-0.05em',
                lineHeight: 1,
              }}
            >
              {discountedPrice ? formatPrice(discountedPrice) : formatPrice(plan.price)}
            </Typography>
            <Typography fontSize="0.72rem" color="text.secondary" fontWeight={600}>
              /{formatDuration(plan.durationInDays)}
            </Typography>
          </Stack>
          {discountedPrice && (
            <Typography
              sx={{
                fontSize: '0.72rem',
                color: 'error.main',
                textDecoration: 'line-through',
                fontWeight: 600,
              }}
            >
              {formatPrice(plan.price)}
            </Typography>
          )}

          <Stack direction="row" spacing={1} sx={{ my: 3, flexWrap: 'wrap', gap: 1 }}>
            <Chip
              icon={<CapacityIcon sx={{ fontSize: 13 }} />}
              label={`${plan.stdCount} students`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                fontWeight: 700,
                fontSize: '0.68rem',
                height: 24,
                borderRadius: 1,
              }}
            />
            <Chip
              icon={<PeopleIcon sx={{ fontSize: 13 }} />}
              label={`${plan.empCount} teachers`}
              size="small"
              sx={{
                bgcolor: alpha('#8b5cf6', 0.08),
                color: '#8b5cf6',
                fontWeight: 700,
                fontSize: '0.68rem',
                height: 24,
                borderRadius: 1,
              }}
            />
          </Stack>

          <Divider sx={{ mb: 2.5, opacity: 0.6 }} />

          <Box sx={{ flex: 1, mb: 2 }}>
            <FeatureList features={plan.features} color={isPopular ? '#6366f1' : '#64748b'} />
          </Box>

          {plan.couponDTO && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha('#22c55e', 0.06),
                border: '1px dashed',
                borderColor: alpha('#22c55e', 0.4),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <DiscountIcon sx={{ fontSize: 16, color: '#22c55e' }} />
                <Typography fontSize="0.72rem" fontWeight={900} color="#16a34a">
                  {discountValue} OFF
                </Typography>
              </Stack>
              {plan.couponDTO.endDate && (
                <Typography fontSize="0.65rem" color="text.secondary" mt={0.5}>
                  Valid till {new Date(plan.couponDTO.endDate).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          )}

          <Button
            variant={isPopular ? 'contained' : 'outlined'}
            fullWidth
            onClick={handleLogin}
            disabled={!isActive}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={{
              py: 1.4,
              borderRadius: 2.5,
              fontWeight: 800,
              fontSize: '0.85rem',
              ...(isPopular
                ? {
                  bgcolor: 'text.primary',
                  color: 'background.paper',
                  '&:hover': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 12px 24px -8px rgba(99,102,241,0.5)',
                  },
                }
                : {
                  color: 'text.primary',
                  borderColor: alpha(theme.palette.text.primary, 0.2),
                  borderWidth: 1.5,
                  '&:hover': {
                    borderColor: 'text.primary',
                    borderWidth: 1.5,
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                  },
                }),
              transition: 'all 0.3s',
            }}
          >
            {isActive ? 'Get started' : 'Notify me'}
          </Button>
        </CardContent>
      </Card>
    </>
  );
});

/* ================================================================== */
/*                       SUBSCRIPTION PLANS                            */
/* ================================================================== */

const SubscriptionPlans = memo(function SubscriptionPlans({ plans }) {
  const activePlans = useMemo(
    () => plans.filter((p) => p.active).filter((plan) => plan.name.includes('Yearly')),
    [plans]
  );

  if (activePlans.length === 0) return null;

  return (
    <>
      <SectionTitle
        title="Honest pricing. No surprises."
        subtitle="Scale up or down as your school grows. Cancel anytime."
        badge="Pricing"
      />
      <Grid container spacing={3} alignItems="stretch">
        {activePlans.map((plan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.id}>
            <PlanCard plan={plan} />
          </Grid>
        ))}
      </Grid>
    </>
  );
});

/* ================================================================== */
/*                        MAIN DASHBOARD                               */
/* ================================================================== */

function SchoolDashboard() {
  const [mode, setMode] = useState('dark');
  const [plans, setPlans] = useState([]);
  const hasFetched = useRef(false);
  const [commandOpen, setCommandOpen] = useState(false);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => setMode((prev) => (prev === 'light' ? 'dark' : 'light')),
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: '#6366f1', light: '#818cf8', dark: '#4338ca' },
          secondary: { main: '#f59e0b' },
          background: {
            default: mode === 'light' ? '#fafaf9' : '#020617',
            paper: mode === 'light' ? '#ffffff' : '#0f172a',
          },
          text: {
            primary: mode === 'light' ? '#0f172a' : '#f8fafc',
            secondary: mode === 'light' ? '#64748b' : '#94a3b8',
          },
          divider: mode === 'light' ? 'rgba(226,232,240,0.9)' : 'rgba(51,65,85,0.5)',
          error: { main: '#ef4444' },
          success: { main: '#22c55e' },
          warning: { main: '#f59e0b' },
        },
        typography: {
          fontFamily:
            '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
          h1: { fontWeight: 900, letterSpacing: '-0.05em' },
          h2: { fontWeight: 900, letterSpacing: '-0.04em' },
          h3: { fontWeight: 900, letterSpacing: '-0.04em' },
          h4: { fontWeight: 900, letterSpacing: '-0.03em' },
          button: { textTransform: 'none', fontWeight: 700 },
        },
        shape: { borderRadius: 12 },
        components: {
          MuiCard: { styleOverrides: { root: { backgroundImage: 'none' } } },
          MuiButton: { styleOverrides: { root: { textTransform: 'none' } } },
          MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: '0.7rem',
                fontWeight: 600,
                borderRadius: 6,
                padding: '6px 10px',
              },
            },
          },
        },
      }),
    [mode]
  );

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadDevice = async () => {
      try {
        const info = await getDeviceInfo();
        localStorage.setItem('device', JSON.stringify(info));
      } catch (error) {
        console.log('Device info error:', error);
      }
    };

    const fetchPlan = async () => {
      try {
        const res = await axios.get(AuthService.pathUrl('GET', '/subscription/plans'));
        setPlans(res?.data || []);
      } catch (error) {
        console.error('Failed to fetch subscription plans:', error.message);
      }
    };

    loadDevice();
    fetchPlan();
  }, []);

  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [authTab, setAuthTab] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleOpenAuth = useCallback(() => {
    setAuthTab(0);
    setAuthDialogOpen(true);
  }, []);

  const handleCloseAuth = useCallback(() => {
    setAuthDialogOpen(false);
    setOpen(false);
    setOpenForm(false);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((s) => ({ ...s, open: false }));
  }, []);

  const handleLogout = useCallback(() => {
    setIsAuthenticated(false);
    setUser(null);
    AuthService.logout();
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, []);

  const handleNavClick = useCallback((tab) => {
    if (tab === 'pdf') setOpen(true);
    else if (tab === 'theme') setMode((m) => (m === 'light' ? 'dark' : 'light'));
    else setAuthTab(tab);
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalAnimations />
        <MeshBackground />

        <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
          <FloatingTopNav
            authTab={authTab}
            setAuthTab={handleNavClick}
            onLoginClick={handleOpenAuth}
            isAuthenticated={isAuthenticated}
            user={user}
            onLogout={handleLogout}
            toggleColorMode={colorMode.toggleColorMode}
            onOpenCommand={() => setCommandOpen(true)}
            onOpenPdf={() => setOpen(true)}
            mode={mode}
          />

          <CommandPalette
            open={commandOpen}
            onClose={() => setCommandOpen(false)}
            onNavigate={handleNavClick}
          />

          {authTab === 0 && (
            <Fade in timeout={800}>
              <Box>
                <MagazineHero onGetStarted={handleOpenAuth} />

                <Container maxWidth="xl" sx={{ pb: 4 }}>
                  <SectionTitle
                    title="Everything your students need."
                    subtitle="From enrollment to graduation — every document, exam, and test, automated."
                    badge="For Students · 8 modules"
                  />
                  <Grid container spacing={2.5}>
                    {studentCards.map((card, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <FeatureCard {...card} />
                      </Grid>
                    ))}
                  </Grid>

                  <SectionTitle
                    title="Built for the people who teach."
                    subtitle="HR tools that free your teachers to actually teach."
                    badge="For Staff · 6 modules"
                  />
                  <Grid container spacing={2.5}>
                    {teacherCards.map((card, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={idx}>
                        <FeatureCard {...card} />
                      </Grid>
                    ))}
                  </Grid>
                  {plans.length > 0 && (
                    <SubscriptionPlans plans={plans} />
                  )}
                  <SectionTitle
                    title="Every role gets exactly what it needs."
                    subtitle="Granular RBAC ensures no data leaks, no confusion."
                    badge="Security · RBAC"
                  />
                  <RbacTable />

                  <SectionTitle
                    title="Verify any certificate in one scan."
                    subtitle="Blockchain-backed authenticity. Try the live demo."
                    badge="Live Demo · Interactive"
                  />
                  <QrDemo />

                  <SectionTitle
                    title="Run your school on autopilot."
                    subtitle="Scheduling, exams, communication — all handled."
                    badge="Smart Ops · 4 tools"
                  />
                  <Grid container spacing={2.5}>
                    {classMgmtCards.map((card, idx) => (
                      <Grid item xs={12} sm={6} md={3} key={idx}>
                        <FeatureCard {...card} />
                      </Grid>
                    ))}
                  </Grid>

                  <BulkOperations onSuccess={() => setOpenForm(true)} />
                  <Footer />
                </Container>
              </Box>
            </Fade>
          )}

          {authTab !== 0 && (
            <Container maxWidth="xl" sx={{ pt: 14, pb: 4 }}>
              {authTab === 1 && (
                <OfferLetterGenerator onSuccess={() => setTimeout(() => setAuthTab(1), 5000)} />
              )}
              {authTab === 2 && (
                <AboutUs onSuccess={() => setTimeout(() => setAuthTab(2), 5000)} />
              )}
              {authTab === 3 && (
                <CheckResultPage onSuccess={() => setTimeout(() => setAuthTab(3), 5000)} />
              )}
              <Footer />
            </Container>
          )}
        </Box>

        <AuthDialog
          open={authDialogOpen}
          onClose={handleCloseAuth}
          onAuthSuccess={(userData) => {
            setIsAuthenticated(true);
            setUser(userData);
            setSnackbar({
              open: true,
              message: `Welcome ${userData.email}!`,
              severity: 'success',
            });
          }}
          initialTab={authTab}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </ThemeProvider>

      <ModalView
        open={open}
        handleClose={handleCloseAuth}
        title="Image To PDF Converter"
        content={<ImageToPDFConverter />}
      />

      <ModalView
        open={openForm}
        handleClose={handleCloseAuth}
        content={<ContactForm onSuccess={() => setOpenForm(false)} />}
      />
    </ColorModeContext.Provider>
  );
}

export default SchoolDashboard;